import { ActionButtonProps } from '@euler/app/components/BottomAction';
import {
  IPadPageContainerContext,
  iPadPageSlot,
  PageContainerForIpad,
} from '@euler/app/components/PageContainerForIpad';
import { Typography } from '@euler/app/components/typography';
import { useTaskNavigation } from '@euler/app/flows/task/functions/useTaskNavigation';
import { ConstructionJobProductList } from '@euler/app/flows/task/pages/construction/components/ConstructionJobProductList';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { orgMemberToProductAuthor } from '@euler/functions/orgMemberToProductAuthor';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { OrgMember } from '@euler/model/entity';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import {
  ConstructionJobProcedureStaged,
  TaskProductManifestItem,
} from '@euler/model/task-detail';
import { showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback, useContext, useMemo, useRef } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  ScrollView,
  StyleProp,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Fill } from 'react-slot-fill';
import { useConstructionJob } from '../functions/useConstructionJob';
import { ConstructionJobGradientPanel } from './ConstructionJobGradientPanel';
import { ConstructionJobProcedureCell } from './ConstructionJobProcedureCell';
import { WiredConstructionJobResolveList } from './ConstructionJobResolveList';
import { ConstructionJobTechnicianBottomSheetModal } from './ConstructionJobTechnicianBottomSheetModal';

const kCellMargin = 20;
const kEnableLandscapeLayout = false;

type CellModel =
  | {
      type: 'procedure';
      procedure: ConstructionJobProcedureStaged;
      index: number;
    }
  | { type: 'remark' }
  | { type: 'resolve-list' }
  | { type: 'product-list' };

export const WiredConstructionJobScreenForIpad = memo(
  ({ jobId }: { jobId: number }) => {
    const { rpx } = useResponsiveContext();
    const props = useConstructionJob(jobId);

    const {
      job,
      staged,
      detail,
      editable,
      onSave,
      onFinish,
      showActions,
      isTechModalVisible,
      onTechnicianPickerPress,
      onTechnicianSelected,
      onTechnicianModalDismiss,
      profile: { user },
    } = props;

    const moreButtonRef = useRef(null);

    const actions = useMemo(() => {
      const res: ActionButtonProps[] = [
        {
          bordered: false,
          text: '',
          variant: 'white',
          onPress: () => showActions(moreButtonRef),
          style: {
            paddingLeft: rpx(15),
            paddingRight: rpx(15),
            backgroundColor: 'transparent',
          },
          buttonRef: moreButtonRef,
          iconRight: (
            <Icon name="more-vertical" size={rpx(24)} color={Colors.White} />
          ),
        },
        {
          bordered: true,
          text: '保存',
          variant: 'white',
          onPress: onSave,
          style: { marginLeft: rpx(15) },
        },
      ];
      if (job?.status !== InspectionTaskJobStatus.Finished) {
        res.push({
          bordered: true,
          text: '完成施工',
          variant: 'white',
          onPress: onFinish,
          style: { marginLeft: rpx(15) },
        });
      }
      return res;
    }, [job?.status, onFinish, onSave, rpx, showActions]);

    const isLandscape = useIsLandscape() && kEnableLandscapeLayout;
    const pageId = `construction-job:${jobId}`;

    return (
      <PageContainerForIpad
        name={pageId}
        title={'施工反馈'}
        subTitle={job?.name}
        actions={editable ? actions : undefined}
        footer={editable ? (isLandscape ? 'default' : 'floating') : 'hidden'}
      >
        <Content {...props} pageId={pageId} isLandscape={isLandscape} />
        <Fill name={iPadPageSlot(pageId, 'header/right')}>
          <View
            css={`
              align-items: flex-end;
            `}
          >
            <Typography.Label
              bold
              size={rpx(28)}
              color={'white'}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {detail.basicInfo.licensePlateNo}
            </Typography.Label>
            <Typography.Label
              regular
              size={rpx(16)}
              color={Colors.White4}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {detail.basicInfo.vehicleName}
            </Typography.Label>
          </View>
        </Fill>

        {editable && (
          <Fill name={iPadPageSlot(pageId, 'footer/left')}>
            <TouchableOpacity
              onPress={onTechnicianPickerPress}
              css={`
                flex-direction: row;
                align-items: center;
              `}
            >
              <Typography.Label
                light
                color={Colors.White7_5}
                size={rpx(18)}
                lineHeight={rpx(24)}
              >
                检测技师: {staged.technicianName ?? user.name}
              </Typography.Label>
              {editable && (
                <Icon
                  name="pencil"
                  color={Colors.White7_5}
                  size={rpx(12)}
                  css={`
                    margin-left: ${rpx(8)}px;
                  `}
                />
              )}
            </TouchableOpacity>
          </Fill>
        )}

        <ConstructionJobTechnicianBottomSheetModal
          visible={isTechModalVisible}
          technicianId={staged.technicianId}
          onSelect={editable ? onTechnicianSelected : undefined}
          onDismiss={onTechnicianModalDismiss}
        />
      </PageContainerForIpad>
    );
  },
);

const Content = memo(
  ({
    isLandscape,
    ...props
  }: ReturnType<typeof useConstructionJob> & {
    pageId: string;
    isLandscape: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width, height } = useContainerLayout();
    const cellWidth = Math.round(width / 3.25);
    const cellHeight = Math.min(cellWidth + rpx(40), height - rpx(80));

    const {
      staged,
      editable,
      jobManager,
      profile: { user },
      onRemarkTextChange,
      onResolveItemPress,
      onTakeMedia,
      onDeleteMedia,
      onEditMedia,
      onRetryMediaUpload,
      onPreviewVideo,
      onRemoveProcedure,
      onProcedureMove,
      onUpdateProductAdoptionConfirmation,
      presentProductAdoptionConfirmation,
    } = props;

    const onProductManifestChange = useCallback(() => {
      jobManager.dirty$.next(true);
      setTimeout(() => {
        presentProductAdoptionConfirmation();
      }, 250);
    }, [jobManager.dirty$, presentProductAdoptionConfirmation]);

    const navigation = useTaskNavigation();
    const appLoading = useAppLoading();
    const onEditCloser = useCallback(
      (item: TaskProductManifestItem) => {
        navigation.push('_selectCloser', {
          title: '选择成交人',
          onSelect: async (member: OrgMember) => {
            const newCloser = orgMemberToProductAuthor(member);
            navigation.goBack();
            try {
              await jobManager.taskManager.productManifestManager.changeManifestItemCloser(
                item.id,
                newCloser,
              );
              appLoading.show({
                type: 'success',
                message: '已更新',
                duration: 1000,
              });
            } catch (e) {
              console.warn('[construction-job] change-closer failed', e);
              void showAlert('更改成交人失败', '请稍后重试');
            }
          },
        });
      },
      [appLoading, jobManager.taskManager.productManifestManager, navigation],
    );

    const cells = useMemo(() => {
      const res = staged.procedures.map<CellModel>((procedure, index) => ({
        type: 'procedure',
        procedure,
        index,
      }));
      if (isLandscape) {
        res.push({ type: 'remark' }, { type: 'resolve-list' });
        res.unshift({ type: 'product-list' });
      }
      return res;
    }, [isLandscape, staged.procedures]);

    const cellMargin = rpx(kCellMargin);

    const cellStyle = useMemo<StyleProp<ViewStyle>>(() => {
      return isLandscape
        ? {
            width: cellWidth,
            height: cellHeight,
            marginLeft: cellMargin,
            marginRight: cellMargin,
          }
        : {
            marginBottom: cellMargin,
            marginLeft: cellMargin,
          };
    }, [cellHeight, cellMargin, cellWidth, isLandscape]);

    const getItemLayout = useCallback(
      (_: any, index: number) => {
        const length = cellWidth + (isLandscape ? cellMargin * 2 : cellMargin);
        return {
          length,
          offset: length * index,
          index,
        };
      },
      [cellMargin, cellWidth, isLandscape],
    );

    const renderItem: ListRenderItem<CellModel> = usePersistFn(({ item }) => {
      if (item.type === 'product-list') {
        return (
          <ConstructionJobProductList
            jobId={props.jobId}
            items={props.productManifestItems ?? []}
            subject={staged.name}
            context={staged.context}
            onChange={onProductManifestChange}
            onUpdateProductAdoptionConfirmation={
              onUpdateProductAdoptionConfirmation
            }
            onEditCloser={onEditCloser}
          />
        );
      }

      if (item.type === 'remark') {
        return (
          <RemarkCell
            editable={editable}
            text={staged.remark ?? ''}
            onChangeText={onRemarkTextChange}
            style={cellStyle}
            fill
          />
        );
      }

      if (item.type === 'resolve-list') {
        return (
          <ScrollView
            style={cellStyle}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
            css={`
              flex-grow: 0;
              border-radius: ${rpx(8)}px;
              overflow: hidden;
            `}
            contentContainerStyle={{
              borderRadius: rpx(8),
              overflow: 'hidden',
            }}
          >
            <WiredConstructionJobResolveList
              jobName={staged.name}
              resolves={staged.context?.resolved}
              editable={editable}
              onItemPress={editable ? onResolveItemPress : undefined}
            />
          </ScrollView>
        );
      }

      const { procedure, index } = item;
      return (
        <ConstructionJobProcedureCell
          key={procedure.name}
          editable={editable}
          index={index}
          first={index === 0}
          last={index === staged.procedures.length - 1}
          procedure={procedure}
          animationOrientation="vertical"
          onTakeMedia={onTakeMedia}
          onDeleteMedia={onDeleteMedia}
          onEditMedia={onEditMedia}
          onPreviewVideo={onPreviewVideo}
          onRetryMediaUpload={onRetryMediaUpload}
          onMove={onProcedureMove}
          onRemove={onRemoveProcedure}
          css={`
            background-color: #fff;
            border-radius: ${rpx(15)}px;
            padding-bottom: ${rpx(15)}px;
          `}
          style={cellStyle}
          headerStyle={{ marginBottom: rpx(15) }}
          titleNumberStyle={{ fontSize: rpx(32), lineHeight: rpx(36) }}
          titleStyle={{ fontSize: rpx(24), lineHeight: rpx(36) }}
        />
      );
    });

    const { footerHeight } = useContext(IPadPageContainerContext);

    return (
      <>
        {isLandscape ? (
          <FlatList
            data={cells}
            renderItem={renderItem}
            horizontal
            showsHorizontalScrollIndicator={Platform.OS === 'web'}
            getItemLayout={getItemLayout}
            contentContainerStyle={{ alignItems: 'center' }}
            css={`
              flex-grow: 0;
              height: ${height}px;
            `}
          />
        ) : (
          <HStack
            alignItems="stretch"
            css={`
              flex: 1;
            `}
          >
            <View
              css={`
                flex: 3;
              `}
            >
              <FlatList
                data={cells}
                renderItem={renderItem}
                showsVerticalScrollIndicator={Platform.OS === 'web'}
                getItemLayout={getItemLayout}
                contentContainerStyle={{
                  paddingBottom: footerHeight ?? 0,
                }}
              />
            </View>
            <ScrollView
              showsVerticalScrollIndicator={Platform.OS === 'web'}
              contentContainerStyle={{
                paddingBottom: footerHeight ?? 0,
              }}
              css={`
                flex: 2;
                margin-left: ${rpx(15)}px;
                margin-right: ${rpx(20)}px;
              `}
            >
              <ConstructionJobProductList
                jobId={props.jobId}
                subject={staged.name}
                items={props.productManifestItems ?? []}
                context={staged.context}
                style={cellStyle}
                onChange={onProductManifestChange}
                onUpdateProductAdoptionConfirmation={
                  onUpdateProductAdoptionConfirmation
                }
                onEditCloser={onEditCloser}
                css={`
                  background-color: #fff;
                  border-radius: ${rpx(8)}px;
                  padding-top: 0;
                `}
                contentContainerStyle={{
                  paddingHorizontal: rpx(15),
                  paddingBottom: rpx(10),
                }}
              />
              <RemarkCell
                editable={editable}
                text={staged.remark ?? ''}
                onChangeText={onRemarkTextChange}
                style={cellStyle}
              />
              <WiredConstructionJobResolveList
                jobName={staged.name}
                resolves={staged.context?.resolved}
                editable={editable}
                onItemPress={editable ? onResolveItemPress : undefined}
                style={cellStyle}
              />
              <Typography.Label
                light
                color={Colors.White7_5}
                size={rpx(18)}
                lineHeight={rpx(24)}
                css={`
                  text-align: center;
                `}
              >
                检测技师: {user.name}
              </Typography.Label>
            </ScrollView>
          </HStack>
        )}
      </>
    );
  },
);

const RemarkCell = memo(
  ({
    text,
    editable,
    fill,
    style,
    onChangeText,
  }: {
    editable: boolean;
    text: string;
    style?: StyleProp<ViewStyle>;
    fill?: boolean;
    onChangeText: (text: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <ConstructionJobGradientPanel
        title="技师备注"
        remark={editable ? '可不填写' : undefined}
        style={style}
      >
        {editable ? (
          <TextInput
            multiline
            value={text}
            onChangeText={onChangeText}
            textAlignVertical="top"
            css={`
              border-color: #9bc5f2;
              border-width: 1px;
              padding: ${rpx(10)}px;
              margin: 0 ${rpx(15)}px ${rpx(15)}px ${rpx(15)}px;
              border-radius: ${rpx(5)}px;
            `}
            style={fill ? { flex: 1 } : { height: rpx(163) }}
          />
        ) : (
          <View
            css={`
              padding: 0 ${rpx(15)}px ${rpx(15)}px ${rpx(15)}px;
            `}
          >
            <Typography.Label
              numberOfLines={0}
              light
              size={rpx(15)}
              color="#666"
            >
              {text ? text : '暂无备注'}
            </Typography.Label>
          </View>
        )}
      </ConstructionJobGradientPanel>
    );
  },
);
